/**
 * ManagerSubmissionsDrawer — the PMO review queue for manager-proposed Work
 * Library checklists. A DM who needs a checklist that doesn't exist authors it
 * from the planner; it lands here as a private item flagged `proposedForOrg`.
 * PMO sees who added it, why (the submission note), the full checklist content,
 * and either publishes it to the org master (Approve) or keeps it private to
 * the creator (Keep private / decline). Governance surface — no field impact.
 */
import { Drawer, Empty, message } from 'antd';
import { format } from 'date-fns';
import { CheckCircle2, Eye, Lock } from 'lucide-react';
import { useMemo, useState } from 'react';
import { SOVEREIGN } from '../../../utils/epcProjectVisual';
import { useUpdateLibraryItem } from '../../hooks/useWorkLibrary';
import type { WorkLibraryItem, WorkLibraryKind } from '../../../../../services/epcWorkLibraryService';
import {
  GateDetailDrawer,
  type GateViewModel,
} from '../planbuilder/gates/GateDetailDrawer';
import {
  educationGateFromLibrary,
  qaqcGateFromLibrary,
  safetyGateFromLibrary,
} from '../planbuilder/gates/gateLibrary';

const KIND_META: Record<
  WorkLibraryKind,
  { label: string; chip: React.CSSProperties }
> = {
  education: { label: 'Education', chip: { background: 'rgba(51,146,116,0.12)', color: '#1F5A47' } },
  safety: { label: 'Safety', chip: { background: 'rgba(201,169,97,0.18)', color: '#8A5A08' } },
  qaqc: { label: 'QA/QC', chip: { background: 'rgba(11,59,46,0.10)', color: '#0B3B2E' } },
  manpower: { label: 'Manpower', chip: { background: 'rgba(11,59,46,0.06)', color: '#475569' } },
  machine: { label: 'Machine', chip: { background: 'rgba(11,59,46,0.06)', color: '#475569' } },
  material: { label: 'Material', chip: { background: 'rgba(11,59,46,0.06)', color: '#475569' } },
};

function isGateKind(kind: WorkLibraryKind): kind is 'education' | 'safety' | 'qaqc' {
  return kind === 'education' || kind === 'safety' || kind === 'qaqc';
}

function gateViewFromItem(item: WorkLibraryItem): GateViewModel | null {
  if (item.kind === 'education') return { kind: 'education', gate: educationGateFromLibrary(item) };
  if (item.kind === 'safety') return { kind: 'safety', gate: safetyGateFromLibrary(item) };
  if (item.kind === 'qaqc') return { kind: 'qaqc', gate: qaqcGateFromLibrary(item) };
  return null;
}

function formatDate(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return format(d, 'dd MMM yyyy');
}

interface SubmissionCardProps {
  item: WorkLibraryItem;
  onView: (view: GateViewModel) => void;
}

function SubmissionCard({ item, onView }: SubmissionCardProps) {
  const update = useUpdateLibraryItem();
  const meta = KIND_META[item.kind];
  const creator = item.createdBy?.name;
  const dateLabel = formatDate(item.createdAt);
  const gateKind = isGateKind(item.kind);

  const approve = () => {
    update.mutate(
      { id: item.id, input: { visibility: 'org' } },
      {
        onSuccess: () =>
          message.success('Published to the master library — available to all managers'),
        onError: (e) =>
          message.error(e instanceof Error ? e.message : 'Could not publish this submission'),
      },
    );
  };

  const keepPrivate = () => {
    update.mutate(
      { id: item.id, input: { proposedForOrg: false } },
      {
        onSuccess: () => message.success(`Kept private to ${creator ?? 'the creator'}`),
        onError: (e) =>
          message.error(e instanceof Error ? e.message : 'Could not update this submission'),
      },
    );
  };

  const handleView = () => {
    const view = gateViewFromItem(item);
    if (view) onView(view);
  };

  return (
    <div
      className="rounded-2xl p-4"
      style={{
        background: SOVEREIGN.cardBg,
        border: `1px solid ${SOVEREIGN.cardBorder}`,
        boxShadow: SOVEREIGN.cardShadow,
      }}
    >
      {/* Kind chip + label */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span
            className="inline-block rounded-full px-2 py-px text-[10px] font-bold uppercase tracking-wide"
            style={meta.chip}
          >
            {meta.label}
          </span>
          <h4 className="mt-1.5 text-[14px] font-extrabold leading-snug" style={{ color: SOVEREIGN.title }}>
            {item.label}
          </h4>
        </div>
        <span
          className="shrink-0 rounded-full px-2 py-px text-[10px] font-semibold"
          style={{ background: 'rgba(201,169,97,0.16)', color: '#9A7D3C' }}
        >
          {item.disciplineLabel ?? 'Common'}
        </span>
      </div>

      {/* Who + when */}
      <p className="mt-2 text-[11.5px]" style={{ color: SOVEREIGN.kpiCaption }}>
        Added by{' '}
        <span className="font-semibold" style={{ color: SOVEREIGN.bottle }}>
          {creator ?? 'a manager'}
        </span>
        {dateLabel ? ` · ${dateLabel}` : ''}
      </p>

      {/* Why note */}
      {item.submissionNote?.trim() ? (
        <blockquote
          className="mt-2.5 rounded-lg px-3 py-2 text-[12px] leading-relaxed"
          style={{
            background: '#F8F2E2',
            color: SOVEREIGN.title,
            borderLeft: `3px solid ${SOVEREIGN.champagne}`,
          }}
        >
          <span className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: SOVEREIGN.kpiCaption }}>
            Why
          </span>
          <p className="mt-0.5 whitespace-pre-wrap italic">“{item.submissionNote.trim()}”</p>
        </blockquote>
      ) : (
        <p className="mt-2.5 text-[11.5px] italic" style={{ color: '#94A3B8' }}>
          No note provided.
        </p>
      )}

      {/* Actions */}
      <div className="mt-3.5 flex flex-wrap items-center gap-2">
        {gateKind ? (
          <button
            type="button"
            onClick={handleView}
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors"
            style={{
              background: '#fff',
              color: SOVEREIGN.bottle,
              border: `1px solid ${SOVEREIGN.cardBorder}`,
            }}
          >
            <Eye size={13} aria-hidden /> View full checklist
          </button>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={keepPrivate}
            disabled={update.isPending}
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-50"
            style={{ background: 'transparent', color: SOVEREIGN.kpiCaption }}
          >
            <Lock size={13} aria-hidden /> Keep private
          </button>
          <button
            type="button"
            onClick={approve}
            disabled={update.isPending}
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-50"
            style={{ background: SOVEREIGN.title, color: '#F8F2E2' }}
          >
            <CheckCircle2 size={13} aria-hidden /> Approve to master
          </button>
        </div>
      </div>
    </div>
  );
}

export interface ManagerSubmissionsDrawerProps {
  open: boolean;
  onClose: () => void;
  items: WorkLibraryItem[];
}

export function ManagerSubmissionsDrawer({ open, onClose, items }: ManagerSubmissionsDrawerProps) {
  const [viewer, setViewer] = useState<GateViewModel | null>(null);

  const sorted = useMemo(
    () =>
      [...items].sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      }),
    [items],
  );

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width={520}
        title="Manager submissions — for the master library"
        styles={{ body: { background: '#FDFAF3' } }}
      >
        {sorted.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: SOVEREIGN.kpiCaption }}>
                No pending submissions — managers propose new checklists from their day planner.
              </span>
            }
          />
        ) : (
          <div className="flex flex-col gap-4">
            {sorted.map((item) => (
              <SubmissionCard key={item.id} item={item} onView={setViewer} />
            ))}
          </div>
        )}
      </Drawer>

      {/* Read-only full-checklist viewer (nested) */}
      <GateDetailDrawer view={viewer} attached={false} onClose={() => setViewer(null)} />
    </>
  );
}
